import { CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { useAuth } from '../../../../hooks/use-auth'
import { useP2PDashboard } from '../../../../hooks/use-p2pDashboard'
import { hasPermission, AuthFunctions } from '../../../../utils/permissions'
import { PdfReportShell } from '../../../customComponents/PdfReportShell'
import { ScopeFilter } from '../ScopeFilter'
import { DashboardBanner } from './DashboardBanner'
import { GoalPanel } from './GoalPanel'
import { GoalsSidebar } from './GoalsSidebar'
import { ProgramBreakdownTable } from './ProgramBreakdownTable'
import { goalViewFor, type Goal } from './goalViews'
import { scopeFilterOptions } from './dashboardScope'
import { styles } from './Dashboard.styles'

const DEFAULT_GOAL: Goal = 'Increase & Maintain'

export const Dashboard = (): JSX.Element => {
  const { user } = useAuth()
  const isDataManager = user != null && (hasPermission(user, AuthFunctions.PEER_TO_PEER_DATA_MANAGEMENT) || hasPermission(user, AuthFunctions.PEER_TO_PEER_DATA_MANAGEMENT_EDIT))
  // The user's ceiling is the highest level they can access. The dashboard defaults to this
  // whole scope; the "Filter by" only drills DOWN to levels below it — never the user's own
  // level or above (an RCN admin filters by ISD/District, not RCN or Statewide).
  const ceiling = isDataManager ? 'State' : (user?.currentOrgType ?? 'ISD')
  const rootOrgId = isDataManager ? 0 : (user?.currentOrgId ?? 0)

  // The Goals rail is a view switcher, not a row filter (FR-DF-008): picking a goal swaps the
  // tiles, chart and breakdown columns over the same set of programs.
  const [activeGoal, setActiveGoal] = useState<Goal>(DEFAULT_GOAL)
  const [filterLevel, setFilterLevel] = useState<string>(ceiling)
  const [filterOrgId, setFilterOrgId] = useState<number | 'all'>('all')

  // The org the dashboard is scoped to: a specific picked org, else the caller's whole scope
  // (statewide org 0 for the Data Manager, the caller's own org otherwise).
  const scopeOrgId = filterOrgId !== 'all' ? filterOrgId : rootOrgId
  const { model, reportingYearId, filterOrgs, isLoading } = useP2PDashboard(scopeOrgId)

  const { levelOptions, orgOptions } = scopeFilterOptions(ceiling, user?.currentOrgId ?? 0, filterLevel, filterOrgs)
  const orgFilter = orgOptions == null
    ? undefined
    : {
        value: String(filterOrgId),
        options: orgOptions,
        onChange: (value: string) => { setFilterOrgId(value === 'all' ? 'all' : Number(value)) },
        testId: 'p2pDashboardFilterOrg'
      }

  if (isLoading) return <CircularProgress />
  if (model == null) return <Typography variant='body1'>No dashboard data available.</Typography>

  const { programBreakdown, totalExistingPrograms } = model.program
  const { layout, tiles, columns, chart } = goalViewFor(activeGoal, model.program)

  return (
    <PdfReportShell variant='plain' hideHeader pdfElementId='p2p-dashboard-pdf'>
      {(_forceExpanded, exportPdf) => (
        <Paper variant='outlined' sx={styles.shell} data-testid='p2pDashboard'>
          <DashboardBanner organizationId={scopeOrgId} reportingYearId={reportingYearId} onPrint={exportPdf} />
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems='stretch'>
            <GoalsSidebar activeGoal={activeGoal} onSelect={setActiveGoal} />
            <Stack sx={styles.content}>
              <Stack direction='row' alignItems='center' gap='12px' flexWrap='wrap' sx={styles.filterBar} data-testid='p2pDashboardFilter'>
                <ScopeFilter
                  levelValue={filterLevel}
                  levelOptions={levelOptions}
                  onLevelChange={level => { setFilterLevel(level); setFilterOrgId('all') }}
                  levelTestId='p2pDashboardFilterLevel'
                  org={orgFilter}
                  count={`${programBreakdown.length} of ${totalExistingPrograms} Programs`}
                  countTestId='p2pProgramCount'
                />
              </Stack>
              <Stack gap='24px' sx={styles.panels}>
                <GoalPanel layout={layout} tiles={tiles} chart={chart} />
                <ProgramBreakdownTable rows={programBreakdown} columns={columns} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      )}
    </PdfReportShell>
  )
}
